import { useEffect, useRef } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightActiveLine, scrollPastEnd } from '@codemirror/view';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import {
  syntaxHighlighting,
  defaultHighlightStyle,
  bracketMatching,
  foldGutter,
  indentOnInput,
  HighlightStyle,
} from '@codemirror/language';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { tags } from '@lezer/highlight';

// ── Custom light theme for FLUX ──

const fluxTheme = EditorView.theme({
  '&': {
    fontSize: '0.76rem',
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'SF Mono', Consolas, monospace",
    height: '100%',
  },
  '.cm-content': {
    padding: '12px 16px',
    lineHeight: '1.45',
    caretColor: '#F59E0B',
  },
  '.cm-cursor': {
    borderLeftColor: '#F59E0B',
    borderLeftWidth: '2px',
  },
  '&.cm-focused .cm-selectionBackground, ::selection': {
    backgroundColor: 'rgba(245, 158, 11, 0.15) !important',
  },
  '.cm-selectionBackground': {
    backgroundColor: 'rgba(245, 158, 11, 0.12) !important',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(245, 158, 11, 0.04)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'rgba(245, 158, 11, 0.08)',
  },
  '.cm-gutters': {
    backgroundColor: '#FAFAFA',
    color: '#B0B0B0',
    borderRight: '1px solid #E8E8E8',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.78rem',
  },
  '.cm-lineNumbers .cm-gutterElement': {
    padding: '0 8px 0 12px',
    minWidth: '36px',
  },
  '.cm-foldGutter .cm-gutterElement': {
    padding: '0 4px',
    cursor: 'pointer',
  },
  '.cm-scroller': {
    overflow: 'auto',
  },
  '&.cm-focused': {
    outline: 'none',
  },
}, { dark: false });

// ── Markdown syntax highlighting ──

const fluxHighlight = HighlightStyle.define([
  // Headings
  { tag: tags.heading1, fontWeight: '700', fontSize: '1.4em', color: '#92400E' },
  { tag: tags.heading2, fontWeight: '700', fontSize: '1.2em', color: '#92400E' },
  { tag: tags.heading3, fontWeight: '600', fontSize: '1.1em', color: '#92400E' },
  { tag: tags.heading4, fontWeight: '600', color: '#92400E' },
  // Emphasis
  { tag: tags.emphasis, fontStyle: 'italic', color: '#6B7280' },
  { tag: tags.strong, fontWeight: '700', color: '#1A1A1A' },
  { tag: tags.strikethrough, textDecoration: 'line-through', color: '#9CA3AF' },
  // Code
  { tag: tags.monospace, fontFamily: "'JetBrains Mono', monospace", backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '3px', padding: '1px 4px' },
  // Links
  { tag: tags.link, color: '#2563EB', textDecoration: 'underline' },
  { tag: tags.url, color: '#2563EB' },
  // Lists
  { tag: tags.list, color: '#F59E0B' },
  // Quotes
  { tag: tags.quote, fontStyle: 'italic', color: '#6B7280', borderLeft: '3px solid #E5E7EB' },
  // Meta (frontmatter --- delimiters, etc.)
  { tag: tags.meta, color: '#78716C' },
  { tag: tags.processingInstruction, color: '#78716C' },
  // Comments
  { tag: tags.comment, color: '#9CA3AF', fontStyle: 'italic' },
]);


interface MarkdownEditorProps {
  content: string;
  onChange: (content: string) => void;
  readOnly?: boolean;
  /** Increment this to force-replace the document (e.g., on file switch) */
  version: number;
}

export function MarkdownEditor({ content, onChange, readOnly = false, version }: MarkdownEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const versionRef = useRef(version);
  const suppressChangeRef = useRef(false);
  const readOnlyCompartment = useRef(new Compartment());

  // Keep onChange ref current without recreating editor
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Create editor exactly once
  useEffect(() => {
    if (!containerRef.current) return;

    const updateListener = EditorView.updateListener.of((update) => {
      if (update.docChanged && !suppressChangeRef.current) {
        onChangeRef.current(update.state.doc.toString());
      }
    });

    const state = EditorState.create({
      doc: '',
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightActiveLine(),
        history(),
        foldGutter(),
        indentOnInput(),
        bracketMatching(),
        scrollPastEnd(),
        markdown({ base: markdownLanguage, codeLanguages: languages }),
        syntaxHighlighting(fluxHighlight),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        fluxTheme,
        keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
        updateListener,
        readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),
        EditorView.lineWrapping,
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle readOnly dynamically via compartment (no editor recreation)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)),
    });
  }, [readOnly]);

  // Swap content on file switch (version change)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (version === versionRef.current) return;
    versionRef.current = version;

    // Suppress onChange during programmatic replacement
    suppressChangeRef.current = true;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: content },
    });
    // Scroll to top on file switch
    view.dispatch({ effects: EditorView.scrollIntoView(0) });
    suppressChangeRef.current = false;
  }, [version, content]);

  return (
    <div
      ref={containerRef}
      className="studio__codemirror"
      id="editor-codemirror"
    />
  );
}
